import { loadDevExtremeStyles } from "./styles";

const DEFAULT_OPTIONS = {
  allowColumnResizing: true,
  columnResizingMode: "widget",
  // The header filter displays a small icon next to columns names, which allows filtering
  // the column values.
  //
  // By default, when enabled, DataGrid displays it for all columns, however,
  // in the `columns` plugin, we set `allowHeaderFiltering: false` by default,
  // so it needs to be enabled by column, which gives us more control.
  //
  // https://js.devexpress.com/jQuery/Documentation/24_1/Guide/UI_Components/DataGrid/Filtering_and_Searching/#Header_Filter
  headerFilter: { visible: true },
  // Performance improvements when there are lot of columns and rows
  scrolling: {
    rowRenderingMode: "virtual",
    columnRenderingMode: "virtual",
  },
  // Hide the toolbar, we prefer having our own custom buttons and triggers.
  toolbar: { visible: false },
  // RGAA 7.1 / WCAG 2.1.1 — DevExtreme disables keyboard navigation by default.
  keyboardNavigation: { enabled: true, enterKeyAction: "moveFocus" },
};

/**
 * @typedef {Object} Plugin
 * @property {String} [id] - A unique identifier to ensure the plugin is overriden if called multiple times.
 * @property {Object} [options] - Options passed to DataGrid instanciation
 * @property {Function} [onDataGridLoaded] - Callback for when the DataGrid module is loaded
 * @property {Function} [onDataGridCreated] - Callback for when the DataGrid is ready
 * @property {Function} [onContentReady] - Call for when the DataGrid content is ready
 * @property {Function} [onOptionChanged] - A function that is executed after a UI component property is changed.
 * @property {Function} [onSelectionChanged] - Call for when the rows selected in the DataGrid change
 * @property {Function} [customizeColumns] - Use this function to make minor adjustments to automatically generated columns.
 */

/**
 * @typedef {[Plugin]} Pack
 */

/** @typedef {Plugin | Pack | ((HTMLElement) => Plugin | Pack)} PluginDefinition */

/**
 *
 * @param {Object} dataGridOptions
 * @param {HTMLElement} dataGridOptions.container
 * @param {[PluginDefinition]} dataGridOptions.plugins
 * @param {String} [dataGridOptions.label] - Accessible label. Falls back to
 *   `container.dataset.datagridLabel` set by the Elixir wrapper.
 */
export async function create(options) {
  const { container, plugins: pluginDefs, label: optionLabel } = options;

  if (!(container instanceof HTMLElement)) {
    throw new Error("`container` is not an HTMLElement", { cause: container });
  }

  const label = optionLabel ?? container.getAttribute("data-datagrid-label");
  if (!label) {
    // RGAA 5.6 — every DataGrid needs an accessible name. Loud in dev, silent in prod.
    console.warn("[a11y] DataGrid created without an accessible label", container);
  }

  const plugins = toPlugins(pluginDefs, container);

  const pluginOptions = mapPlugins(plugins, "options", null);
  const defaultOptions = { ...DEFAULT_OPTIONS };
  const dataGridOptions = Object.assign(defaultOptions, ...pluginOptions);

  try {
    const [{ default: DataGrid }] = await Promise.all([import("devextreme/ui/data_grid"), loadDevExtremeStyles()]);
    mapPlugins(plugins, "onDataGridLoaded", null, DataGrid);

    const $grid = container.querySelector(".js-datagrid-grid");
    const dataGrid = new DataGrid($grid, {
      ...dataGridOptions,
      customizeColumns: pluginsReducer(plugins, "customizeColumns"),
      onContentReady: function (...args) {
        applyGridAriaLabel($grid, label);
        pluginsMapper(plugins, "onContentReady").apply(this, args);
      },
      onOptionChanged: pluginsMapper(plugins, "onOptionChanged"),
      onSelectionChanged: pluginsMapper(plugins, "onSelectionChanged"),
    });

    mapPlugins(plugins, "onDataGridCreated", dataGrid);

    return dataGrid;
  } catch (error) {
    console.error(error);
    throw `Impossible to load DataGrid : ${error}`;
  }
}

/**
 * DevExtreme renders the actual grid table deep inside its widget DOM
 * (`.dx-datagrid-content[role="grid"]`) and leaves that element without an
 * accessible name. The `elementAttr` option only reaches the widget root,
 * which DevExtreme then downgrades to `role="presentation"`. So we propagate
 * the label to the real `[role="grid"]` element here — after every content
 * render, because DevExtreme may re-create the inner DOM on virtual scroll,
 * sort, filter, etc.
 */
export function applyGridAriaLabel($grid, label) {
  if (!label) return;
  const inner = $grid.querySelector('[role="grid"]');
  if (inner && inner.getAttribute("aria-label") !== label) {
    inner.setAttribute("aria-label", label);
  }
}

function toPlugins(pluginDefs, container) {
  return pluginDefs.flatMap((pluginDef) => {
    const pluginOrPack = typeof pluginDef === "function" ? pluginDef(container) : pluginDef;
    return Array.isArray(pluginOrPack) ? toPlugins(pluginOrPack, container) : [pluginOrPack];
  });
}

function mapPlugins(plugins, callback, self, ...args) {
  return plugins
    .filter((plugin) => pluginHasCallback(plugin, callback))
    .map((plugin) => callPluginCallback(plugin[callback], self, args));
}

function pluginsMapper(plugins, callback) {
  // Do not convert to arrow function, `this` would be lost
  return function (...args) {
    mapPlugins(plugins, callback, this, ...args);
  };
}

function reducePlugins(plugins, callback, self, initialValue) {
  return plugins
    .filter((plugin) => pluginHasCallback(plugin, callback))
    .reduce((accumulator, plugin) => callPluginCallback(plugin[callback], self, [accumulator]), initialValue);
}

function pluginsReducer(plugins, callback) {
  return function (accumulator) {
    return reducePlugins(plugins, callback, this, accumulator);
  };
}

function pluginHasCallback(plugin, callback) {
  return Object.hasOwn(plugin, callback);
}

function callPluginCallback(callback, self, args) {
  return typeof callback === "function" ? callback.apply(self, args) : callback;
}
